import { Box, Button, Select, Flex, Form, Input, Tag } from '@cowsquare/design';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Field, Form as FinalForm } from 'react-final-form';
import { getHTMLString } from '../../../../utils/getHTMLString';
import { useAdsQuery } from '../../../../utils/hooks';
import { Advertisements } from '../../../Advertisements';
import { EventCardList } from '../../../EventCardList';
import { NoResult } from '../../../NoResult';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import { useQueryParams, StringParam, NumberParam } from 'use-query-params';

const PAGE_SIZE = 21;

const FilterBox = styled(Box)`
  background-color: ${themeGet('colors.whites.0')};
`;

const StyledSection = styled(Section)`
  position: relative;
`;

const getCardProps = item => ({
  variant: 'vertical',
  height: 389,
  date: null,
  description: R.pipe(R.propOr('', 'active_ingredients'), getHTMLString)(item),
  coverUrl: R.pathOr('', ['cover', 'link'], item),
  cardInfoData: null,
  tags: R.pipe(R.pathOr('', ['brand', 'name']), name => <Tag key={name}>{name}</Tag>)(item),
});

const GET_DRUGS = gql`
  query getDrugs($limit: Int, $offset: Int, $keyword: String, $classId: uuid, $superClassId: uuid) {
    list: hkdutest_drugs(
      limit: $limit
      offset: $offset
      where: {
        _or: [
          { name: { _ilike: $keyword } }
          { common_name: { _ilike: $keyword } }
          { chemical_name: { _ilike: $keyword } }
        ]
        class_id: { _eq: $classId }
        class: { super_class_id: { _eq: $superClassId } }
      }
    ) {
      id
      name
      cover {
        link
      }
      active_ingredients
      brand {
        name
      }
    }

    classes: hkdutest_drug_super_class {
      id
      name
      sub_classes: drug_classes {
        id
        name
        super_class_id
      }
    }

    total: hkdutest_drugs_aggregate(
      where: {
        _or: [
          { name: { _ilike: $keyword } }
          { common_name: { _ilike: $keyword } }
          { chemical_name: { _ilike: $keyword } }
        ]
        class_id: { _eq: $classId }
        class: { super_class_id: { _eq: $superClassId } }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

const DEFAULT_OPTION = { name: 'All', value: '' };

const DrugSearchList = ({ uri }) => {
  const { ads } = useAdsQuery(uri);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    keyword: StringParam,
    classId: StringParam,
    superClassId: StringParam,
  });
  const { page = 1, keyword, classId, superClassId } = query;

  return (
    <Query
      query={GET_DRUGS}
      variables={{
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        keyword: keyword ? `%${keyword}%` : undefined,
        classId,
        superClassId,
      }}
      fetchPolicy="no-cache"
    >
      {({ data }) => {
        const list = R.propOr([], 'list', data);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);
        const classes = R.propOr([], 'classes', data);
        const classesOptions = R.pipe(
          R.map(({ name, id }) => ({ name, value: id })),
          R.prepend(DEFAULT_OPTION)
        )(classes);
        const subClassesOptions = R.pipe(
          R.map(R.propOr([], 'sub_classes')),
          R.flatten(),
          R.map(({ name, id, super_class_id }) => ({ name, value: id, super_class_id }))
        )(classes);
        const currentSuperClass = R.find(R.propEq('value', superClassId), classesOptions) || DEFAULT_OPTION;
        const currentClass = R.find(R.propEq('value', classId), subClassesOptions) || DEFAULT_OPTION;

        return (
          <StyledSection>
            <Advertisements ads={ads} />
            <Box pt={8}>
              <Box px={[5, 0]}>
                <Flex justifyContent="space-between">
                  <Title>Drug Search</Title>
                </Flex>
              </Box>
              <FilterBox p={5} my={6}>
                <FinalForm
                  initialValues={{
                    keyword,
                    superClass: currentSuperClass,
                    class: currentClass,
                  }}
                  onSubmit={formValue =>
                    setQuery({
                      keyword: formValue.keyword,
                      classId: R.path(['class', 'value'], formValue),
                      superClassId: R.path(['superClass', 'value'], formValue),
                    })
                  }
                  render={({ handleSubmit, submitting, values }) => (
                    <Form>
                      <Flex flexDirection={['column', 'row']} justifyContent="space-between">
                        <Box flex={1} px={4}>
                          <Field name="superClass">
                            {({ input, meta }) => (
                              <Form.FormItem label="Drug Classification" input={input} meta={meta}>
                                <Select {...input} placeholder="Please select..." options={classesOptions} />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="class">
                            {({ input, meta }) => (
                              <Form.FormItem label="Sub Classification" input={input} meta={meta}>
                                <Select
                                  {...input}
                                  placeholder="Please select..."
                                  options={R.pipe(
                                    R.filter(
                                      R.propEq('super_class_id', R.path(['superClass', 'value'], values))
                                    ),
                                    R.prepend(DEFAULT_OPTION)
                                  )(subClassesOptions)}
                                  disabled={!R.path(['superClass', 'value'], values)}
                                />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="activeIngredients">
                            {({ input, meta }) => (
                              <Form.FormItem label="Active Ingredients" input={input} meta={meta}>
                                <Input {...input} placeholder="Search..." />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="Indications">
                            {({ input, meta }) => (
                              <Form.FormItem label="Indications" input={input} meta={meta}>
                                <Input {...input} placeholder="Search..." />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                        <Box flex={1} px={4}>
                          <Field name="keyword">
                            {({ input, meta }) => (
                              <Form.FormItem label="Keyword" input={input} meta={meta}>
                                <Input {...input} placeholder="Search..." />
                              </Form.FormItem>
                            )}
                          </Field>
                        </Box>
                      </Flex>
                      <Box px={4}>
                        <Flex justifyContent="flex-end">
                          <Box ml={4}>
                            <Button disabled={submitting} onClick={handleSubmit} variant="primary">
                              Submit
                            </Button>
                          </Box>
                        </Flex>
                      </Box>
                    </Form>
                  )}
                />
              </FilterBox>
              <Box pb={8} px={[5, 0]}>
                {list.length === 0 && <NoResult>No result found...</NoResult>}
                <EventCardList
                  uri={uri}
                  page={page}
                  onPageChange={page => {
                    setQuery({ page }, 'pushIn');
                  }}
                  list={list}
                  total={total}
                  options={getCardProps}
                />
              </Box>
            </Box>
          </StyledSection>
        );
      }}
    </Query>
  );
};

export default DrugSearchList;
